import React from 'react';
import { Helmet } from 'react-helmet-async';
import { COMPANY_LEGAL_INFO } from '../../constants/legal';

const SchemaMarkup = () => {
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://scalenix.fr/#website",
    "url": "https://scalenix.fr",
    "name": "ScaleNix",
    "description": "Enterprise Open Source Solutions",
    "inLanguage": ["en", "fr", "de", "es", "it"],
    "publisher": {
      "@id": "https://scalenix.fr/#organization"
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://scalenix.fr/#organization",
    "name": COMPANY_LEGAL_INFO.name,
    "url": "https://scalenix.fr",
    "logo": {
      "@type": "ImageObject",
      "url": "https://scalenix.fr/logo.png",
      "width": "512",
      "height": "512"
    },
    "description": "Expert open source solutions for enterprises. Implementation, migration, and training services to optimize your infrastructure.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": COMPANY_LEGAL_INFO.address.street,
      "addressLocality": COMPANY_LEGAL_INFO.address.city,
      "postalCode": COMPANY_LEGAL_INFO.address.postalCode,
      "addressCountry": COMPANY_LEGAL_INFO.address.country
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+33633609030",
      "contactType": "customer service",
      "email": "contact@scalenix.fr",
      "availableLanguage": ["English", "French", "German", "Spanish", "Italian"]
    },
    "sameAs": [
      "https://www.linkedin.com/company/scalenix",
      "https://github.com/scalenix"
    ],
    "foundingDate": "2024",
    "founder": {
      "@type": "Person",
      "name": COMPANY_LEGAL_INFO.legalRepresentative,
      "jobTitle": COMPANY_LEGAL_INFO.position
    },
    "vatID": COMPANY_LEGAL_INFO.vatNumber,
    "legalName": COMPANY_LEGAL_INFO.name
  };

  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://scalenix.fr/#services",
    "provider": {
      "@type": "Organization",
      "@id": "https://scalenix.fr/#organization"
    },
    "serviceType": "IT Services",
    "areaServed": "Worldwide",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Enterprise Open Source Solutions",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Zimbra Implementation",
            "description": "Expert Zimbra setup and configuration for enterprises",
            "category": "Email & Collaboration",
            "provider": { "@id": "https://scalenix.fr/#organization" }
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "LemonLDAP Integration",
            "description": "SSO and access management solutions",
            "category": "Identity Management",
            "provider": { "@id": "https://scalenix.fr/#organization" }
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Nextcloud Enterprise",
            "description": "Secure cloud collaboration platform",
            "category": "Cloud Storage",
            "provider": { "@id": "https://scalenix.fr/#organization" }
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "OpenLDAP Identity",
            "description": "Enterprise directory services",
            "category": "Identity Management",
            "provider": { "@id": "https://scalenix.fr/#organization" }
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Training Programs",
            "description": "Professional training and certification",
            "category": "Education",
            "provider": { "@id": "https://scalenix.fr/#organization" }
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Enterprise Support",
            "description": "24/7 professional technical support",
            "category": "Support",
            "provider": { "@id": "https://scalenix.fr/#organization" }
          }
        }
      ]
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://scalenix.fr"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Solutions",
        "item": "https://scalenix.fr/#solutions"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Services",
        "item": "https://scalenix.fr/#services"
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": "Training",
        "item": "https://scalenix.fr/#training"
      },
      {
        "@type": "ListItem",
        "position": 5,
        "name": "Cloud",
        "item": "https://scalenix.fr/#cloud"
      },
      {
        "@type": "ListItem",
        "position": 6,
        "name": "Support",
        "item": "https://scalenix.fr/#support"
      }
    ]
  };

  const softwareApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "ScaleNix Enterprise Suite",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "All",
    "offers": {
      "@type": "Offer",
      "price": "Contact for pricing",
      "priceCurrency": "EUR"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "156"
    },
    "featureList": [
      "Email & Collaboration",
      "Identity Management",
      "Cloud Storage",
      "Security",
      "Enterprise Support"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(servicesSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(softwareApplicationSchema)}
      </script>
    </Helmet>
  );
};

export default SchemaMarkup;